import {assert} from 'Asserts';
import {
  MethodNode,
  getMethodComponentCid,
  getMethodComponentName,
  getMethodComponentProcedureParameterId,
  MaterialFlow,
} from 'procedures/ProcedureDomainTypes';
import {TaskDefinitionVersionSpecWithTaskSchemaFlowchart} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {ProcessFlowchartEdgeType} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';

import {isMethodNode} from './MethodNodeUtils';

/**
 * Extracts material flows from a process flowchart by analyzing DATA_FLOW edges between method nodes.
 *
 * This function iterates through all edges in the flowchart and identifies material flows by:
 * 1. Finding DATA_FLOW edges that connect method nodes
 * 2. Extracting material output settings from the source method node
 * 3. Extracting material input settings from the target method node
 * 4. Creating MaterialFlow objects that capture the connection between output and input materials
 *
 * @param flowchart - The process flowchart containing nodes and edges with task definition specs
 * @returns An array of MaterialFlow objects representing material connections between methods
 */
export function getMaterialFlowsFromProcessFlowchart(
  flowchart: TaskDefinitionVersionSpecWithTaskSchemaFlowchart
): Array<MaterialFlow> {
  const materialFlows: Array<MaterialFlow> = [];
  for (const edge of flowchart.edges) {
    if (edge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
      // Find source method
      const sourceNode = flowchart.nodes[edge.fromNodeClientId];
      assert(isMethodNode(sourceNode), 'Expected source node for material edge to be a method node');
      const sourceMethod = sourceNode.newProcessTaskDefinitionVersion.applicationData as MethodNode;

      // Find target method
      const targetNode = flowchart.nodes[edge.toNodeClientId];
      assert(isMethodNode(targetNode), 'Expected target node for material edge to be a method node');
      const targetMethod = targetNode.newProcessTaskDefinitionVersion.applicationData as MethodNode;

      // Find source material — procedure parameter IDs are guaranteed to exist because
      // parameters must be saved before data flow edges can reference them.
      assert(
        edge.fromFieldClientId != null,
        'Expected source field client id to be present for data flow edge'
      );
      const sourceMaterial = sourceMethod.materialOutputSettings.find((setting) => {
        const procedureParameterId = getMethodComponentProcedureParameterId(setting);
        // Null IDs indicate unsaved parameters — skip them as non-matches.
        // The assertion below will catch if no material matches the edge's referenced ID.
        return procedureParameterId != null && procedureParameterId === edge.fromFieldClientId;
      });
      assert(
        sourceMaterial != null,
        `Expected material output with procedure parameter ID '${edge.fromFieldClientId}' to exist on source method '${sourceMethod.name}'`
      );

      // Find target material
      assert(
        edge.toFieldClientId != null,
        'Expected target field client id to be present for data flow edge'
      );
      const targetMaterial = targetMethod.materialInputSettings.find((setting) => {
        const procedureParameterId = getMethodComponentProcedureParameterId(setting);
        // Null IDs indicate unsaved parameters — skip them as non-matches.
        // The assertion below will catch if no material matches the edge's referenced ID.
        return procedureParameterId != null && procedureParameterId === edge.toFieldClientId;
      });
      assert(
        targetMaterial != null,
        `Expected material input with procedure parameter ID '${edge.toFieldClientId}' to exist on target method '${targetMethod.name}'`
      );

      materialFlows.push({
        sourceMaterialCid: getMethodComponentCid(sourceMaterial),
        sourceMaterialName: getMethodComponentName(sourceMaterial),
        sourceMethodName: sourceMethod.name,
        targetMaterialCid: getMethodComponentCid(targetMaterial),
        targetMaterialName: getMethodComponentName(targetMaterial),
        targetMethodName: targetMethod.name,
      });
    }
  }
  return materialFlows;
}
